
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useInventoryStore, InventoryItem } from "@/store/api/inventoryStore";
import { toast } from "sonner";

interface EditItemDialogProps {
  item: InventoryItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Item name must be at least 2 characters.",
  }),
  size: z.string().min(1, {
    message: "Size is required.",
  }),
  stock: z.coerce.number().int().min(0, {
    message: "Stock must be a positive number.",
  }),
  available: z.coerce.number().int().min(0, {
    message: "Available items must be a positive number.",
  }),
  reserved: z.coerce.number().int().min(0, {
    message: "Reserved items must be a positive number.",
  }),
  price: z.coerce.number().min(0, {
    message: "Price must be a positive number.",
  }),
  category: z.string().min(1, {
    message: "Please select a category.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export function EditItemDialog({ item, open, onOpenChange }: EditItemDialogProps) {
  const { t } = useTranslation(["inventory", "common"]);
  const { updateItem } = useInventoryStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: item.name,
      size: item.size,
      stock: item.stock,
      available: item.available,
      reserved: item.reserved,
      price: item.price,
      category: item.category,
    },
  });

  // Brouillon par entité (edit-item:{id}) — le dialog est monté à chaque
  // ouverture, la restauration passe par mergeDraftOver dans le reset
  // ci-dessous. Snapshot complet → l'invariant stock = available + reserved
  // est restauré tout-ou-rien.
  const { clearDraft, mergeDraftOver } = useFormDraft<FormValues>("edit-item", form, {
    entityId: item.id,
    restoreOnMount: false,
  });

  // Update form when item changes — brouillon superposé aux valeurs serveur.
  useEffect(() => {
    form.reset(mergeDraftOver({
      name: item.name,
      size: item.size,
      stock: item.stock,
      available: item.available,
      reserved: item.reserved,
      price: item.price,
      category: item.category,
    }));
  }, [item, form, mergeDraftOver]);

  // Validate stock total equals available + reserved
  const validateStockTotal = (values: FormValues) => {
    return values.stock === values.available + values.reserved;
  };

  const onSubmit = async (values: FormValues) => {
    if (!validateStockTotal(values)) {
      toast.error(t('stockMismatchError'));
      return;
    }

    setIsSubmitting(true);
    try {
      await updateItem({
        ...item,
        ...values,
      });
      toast.success(t('itemUpdatedSuccess'));
      clearDraft();
      onOpenChange(false);
    } catch (error) {
      // Le store lève en cas d'échec : pas de toast de succès sans écriture.
      console.error('[EditItemDialog] updateItem:', error);
      toast.error(t('itemUpdatedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('editItem')}</DialogTitle>
          <DialogDescription>
            {t('editItemDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('itemName')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('size')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('category')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="suits">{t('suits')}</SelectItem>
                        <SelectItem value="equipment">{t('equipment')}</SelectItem>
                        <SelectItem value="accessories">{t('accessories')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('stock')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="available"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('available')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="reserved"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('reserved')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('price')}</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button
                type="button"
                variant="outline"
                onClick={() => { clearDraft(); onOpenChange(false); }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
